import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { ApiError, getJoinOffer, joinAccounts, discardJoinOffer, type JoinOffer } from '../api'
import { joinErrorMessage } from '../joinErrors'

/**
 * What the page says when a join, or the question, got an error. A 5xx
 * decided nothing, so its sentence is the server's "try again", and the
 * question stays on the page with Yes enabled (joinErrorMessage).
 */
export function joinRefusal(e: unknown): string {
  return e instanceof ApiError ? joinErrorMessage(e.code, e.status, e.message) : joinErrorMessage('')
}

/**
 * Two accounts that are the same person.
 *
 * Somebody proves who they are more than one way over time: a code to their
 * address, a Google account, their employer's identity provider once the
 * company connects one. Each proof is its own account, deliberately — nothing
 * in a matching address separates one holder of a mailbox from the next.
 *
 * The cost of that is real and this screen is where it is paid. A document
 * somebody was sent is reached through the account that proved control of the
 * address it was sent to, so someone who signed up with their own address and
 * works somewhere else has two halves of a life here. Joining is the only way
 * to put them together, and the only moment it is safe to offer is this one:
 * one live person, both credentials, one sitting.
 *
 * It is rendered inside the app shell rather than on its own, because the
 * question cannot be answered without knowing who you are already signed in
 * as, and the shell is where that is written. Walking away from it costs
 * nothing: the second sign-in is held for ten minutes and then let go, which
 * is the same outcome as saying no.
 */
export function JoinAccounts({ onJoined }: { onJoined: () => void }) {
  const navigate = useNavigate()
  // null while we are still asking, so neither answer flashes in front of
  // somebody who has not been offered anything.
  const [offer, setOffer] = useState<JoinOffer | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<'' | 'join' | 'discard'>('')

  useEffect(() => {
    getJoinOffer()
      .then((o) => setOffer(o))
      // Not "there is nothing to join": we do not know that. A request that
      // failed is a request that failed, and telling somebody their second
      // account is not there on the strength of a 502 is the kind of confident
      // sentence this product exists not to write.
      .catch((e) => {
        setOffer({ offered: false, signed_in_as: '', also_proved: '' })
        setError(joinRefusal(e))
      })
  }, [])

  const answer = useCallback(
    async (join: boolean) => {
      setBusy(join ? 'join' : 'discard')
      setError('')
      try {
        if (join) {
          await joinAccounts()
          // The memberships changed, so the shell is holding a stale session.
          onJoined()
        } else {
          await discardJoinOffer()
        }
        navigate('/', { replace: true })
      } catch (e) {
        setError(joinRefusal(e))
      } finally {
        setBusy('')
      }
    },
    [navigate, onJoined],
  )

  if (offer === null) {
    return (
      <main className="page page-narrow">
        <p className="muted">Loading…</p>
      </main>
    )
  }

  if (!offer.offered) {
    return (
      <main className="page page-narrow">
        <div className="page-head">
          <h1>Two accounts</h1>
          <p>Combine two sign-ins that belong to the same person.</p>
        </div>
        {error && (
          <div className="notice notice-bad" role="alert">
            {error}
          </div>
        )}
        <div className="card empty">
          <h2>There is nothing to join</h2>
          <p>
            The other sign-in has been used or has expired. Sign in with it again if you want
            to put the two accounts together.
          </p>
          <Link className="btn" to="/">
            Back to your documents
          </Link>
        </div>
      </main>
    )
  }

  return (
    <main className="page page-narrow">
      <div className="page-head">
        <h1>Is this the same person?</h1>
        <p>You signed in with a second account in this browser. Nothing is combined until you confirm.</p>
      </div>

      <div className="card stack">
        {/* Both addresses once, and never again inside a sentence. An address
            somebody typed is a single unbreakable word when it has nothing to
            break at, and interpolating one into prose scrolled this page 269px
            sideways at 390px the first time it was photographed. */}
        <dl className="account-pair">
          <dt>Signed in as</dt>
          <dd className="mono">{offer.signed_in_as}</dd>
          <dt>Also just signed in as</dt>
          <dd className="mono">{offer.also_proved}</dd>
        </dl>

        <p>
          If both are you, joining them makes one account holding everything both
          accounts have: the documents each was sent, and every organization either
          belongs to. You stay signed in as the first one and can sign in either way
          from now on.
        </p>

        <div className="notice notice-warn">
          <p>
            Join them only if both addresses are yours. Everything the other account
            was ever sent becomes readable here, and there is no way back.
          </p>
        </div>

        {error && (
          <div className="notice notice-bad" role="alert">
            {error}
          </div>
        )}

        {/* The safe answer is read first on purpose. The join cannot be taken
            back, and the rule this app already holds for such an action is that
            it must not be reachable by the momentum of reading down the page.
            It is not .btn-danger: red here is a broken seal and means something
            was destroyed, and a join destroys nothing. It is irreversible, which
            is what the notice above it says. */}
        <div className="row">
          <button
            className="btn btn-secondary"
            disabled={busy !== ''}
            onClick={() => void answer(false)}
          >
            {busy === 'discard' ? 'Leaving them…' : 'No, keep them separate'}
          </button>
          <button className="btn" disabled={busy !== ''} onClick={() => void answer(true)}>
            {busy === 'join' ? 'Joining…' : 'Yes, join them'}
          </button>
        </div>
        <p className="small faint">
          Keeping them separate leaves both accounts exactly as they are, and signs this
          browser out of the second one.
        </p>
      </div>
    </main>
  )
}
